import React from 'react';
import { cn } from '@/lib/utils';

/**
 * CoRegulate Icon Components
 * 
 * Line-based, rounded, friendly icons
 * Stroke color: Soft Charcoal (#2A8585)
 * Avoid sharp angles or aggressive shapes
 * Simple metaphors: breath, connection, grounding
 */

/**
 * Icon Container
 * Wrapper with colored background for icons
 */
export const IconContainer = ({
  children,
  className,
  variant = 'sage', // 'sage' | 'rose' | 'lavender' | 'blue' | 'gold' | 'neutral'
  size = 'md', // 'sm' | 'md' | 'lg' | 'xl'
  useGradient = false, // Enable gradient background for icons
  ...props
}) => {
  // Sunset Lake palette variants - solid backgrounds
  const variantStyles = {
    sage: 'bg-[rgba(52,70,72,0.15)] text-[#1D6B6B]',
    eucalyptus: 'bg-[rgba(52,70,72,0.18)] text-[#1D6B6B]',
    rose: 'bg-[rgba(255,187,152,0.25)] text-[#1D6B6B]',
    lavender: 'bg-[rgba(125,142,149,0.18)] text-[#7D8E95]',
    blue: 'bg-[rgba(125,142,149,0.18)] text-[#1D6B6B]',
    gold: 'bg-[rgba(255,187,152,0.25)] text-[#1D6B6B]',
    neutral: 'bg-[rgba(255,187,152,0.2)] text-[#1D6B6B]',
  };

  // Gradient versions - 4 color palette distributed evenly
  const gradientStyles = {
    sage: { background: 'linear-gradient(145deg, #2A8585 0%, #1D6B6B 100%)', color: 'white' },        // Teal
    eucalyptus: { background: 'linear-gradient(145deg, #2A8585 0%, #1D6B6B 100%)', color: 'white' },  // Teal
    rose: { background: 'linear-gradient(145deg, #FFBB98 0%, #E09060 100%)', color: 'white' },        // Coral
    lavender: { background: 'linear-gradient(145deg, #E8A0A0 0%, #C97B7B 100%)', color: 'white' },    // Dusty Rose
    blue: { background: 'linear-gradient(145deg, #2A8585 0%, #1D6B6B 100%)', color: 'white' },        // Teal
    gold: { background: 'linear-gradient(145deg, #FFBB98 0%, #E09060 100%)', color: 'white' },        // Coral
    neutral: { background: 'linear-gradient(145deg, #9AABB8 0%, #7D8E95 100%)', color: 'white' },     // Neutral
  };

  const sizeStyles = {
    sm: 'w-8 h-8 rounded-lg',
    md: 'w-10 h-10 rounded-xl',
    lg: 'w-12 h-12 rounded-xl',
    xl: 'w-14 h-14 rounded-2xl',
  };

  if (useGradient) {
    const gradient = gradientStyles[variant] || gradientStyles.sage;
    return (
      <div
        className={cn(
          'flex items-center justify-center shadow-md',
          sizeStyles[size],
          className
        )}
        style={{ background: gradient.background, color: gradient.color }}
        {...props}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center',
        sizeStyles[size],
        variantStyles[variant],
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};
IconContainer.displayName = 'IconContainer';

/**
 * Brand Icon
 * SVG icon with brand styling applied
 */
export const BrandIcon = ({
  children,
  className,
  size = 20,
  color = 'current', // 'current' | 'primary' | 'secondary' | 'muted'
  strokeWidth = 1.5,
  ...props
}) => {
  const colorStyles = {
    current: 'text-current',
    primary: 'text-[#2A8585]',
    secondary: 'text-[#7D8E95]',
    muted: 'text-[#7D8E95]',
    sage: 'text-[#1D6B6B]',
    rose: 'text-[#FFBB98]',
  };

  // If children is a React element, clone it with brand props
  if (React.isValidElement(children)) {
    return React.cloneElement(children, {
      size,
      strokeWidth,
      className: cn(colorStyles[color], className),
      ...props,
    });
  }

  return (
    <span
      className={cn(colorStyles[color], className)}
      {...props}
    >
      {children}
    </span>
  );
};
BrandIcon.displayName = 'BrandIcon';

/**
 * Custom Brand Icons - Metaphors for regulation
 */

// Breathing icon - gentle wave
export const BreathIcon = ({ size = 24, className, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={cn('text-[#2A8585]', className)}
    {...props}
  >
    <path d="M4 12C4 12 7 8 12 8C17 8 20 12 20 12" />
    <path d="M4 12C4 12 7 16 12 16C17 16 20 12 20 12" />
  </svg>
);

// Grounding icon - roots/connection
export const GroundingIcon = ({ size = 24, className, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={cn('text-[#2A8585]', className)}
    {...props}
  >
    <circle cx="12" cy="8" r="3" />
    <path d="M12 11V14" />
    <path d="M8 18C8 18 10 15 12 15C14 15 16 18 16 18" />
    <path d="M6 21C6 21 9 17 12 17C15 17 18 21 18 21" />
  </svg>
);

// Connection icon - two circles meeting
export const ConnectionIcon = ({ size = 24, className, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={cn('text-[#2A8585]', className)}
    {...props}
  >
    <circle cx="8" cy="12" r="4" />
    <circle cx="16" cy="12" r="4" />
    <path d="M12 10V14" opacity="0.5" />
  </svg>
);

// Safe space icon - gentle shield
export const SafeSpaceIcon = ({ size = 24, className, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={cn('text-[#2A8585]', className)}
    {...props}
  >
    <path d="M12 3C12 3 4 6 4 12C4 18 12 21 12 21C12 21 20 18 20 12C20 6 12 3 12 3Z" />
    <path d="M12 8V12" />
    <circle cx="12" cy="15" r="1" fill="currentColor" />
  </svg>
);

// Heart/Care icon - soft heart
export const CareIcon = ({ size = 24, className, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={cn('text-[#2A8585]', className)}
    {...props}
  >
    <path d="M12 6C9.5 3 5 3.5 4 7C3 10.5 5 13 12 19C19 13 21 10.5 20 7C19 3.5 14.5 3 12 6Z" />
  </svg>
);

// Calm waves icon
export const CalmIcon = ({ size = 24, className, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={cn('text-[#2A8585]', className)}
    {...props}
  >
    <path d="M3 8C5 6 7 6 9 8C11 10 13 10 15 8C17 6 19 6 21 8" />
    <path d="M3 12C5 10 7 10 9 12C11 14 13 14 15 12C17 10 19 10 21 12" />
    <path d="M3 16C5 14 7 14 9 16C11 18 13 18 15 16C17 14 19 14 21 16" />
  </svg>
);

// Speech/Communication icon
export const CommunicateIcon = ({ size = 24, className, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={cn('text-[#2A8585]', className)}
    {...props}
  >
    <path d="M4 6C4 5 5 4 6 4H18C19 4 20 5 20 6V14C20 15 19 16 18 16H8L4 20V6Z" />
    <circle cx="9" cy="10" r="1" fill="currentColor" />
    <circle cx="12" cy="10" r="1" fill="currentColor" />
    <circle cx="15" cy="10" r="1" fill="currentColor" />
  </svg>
);

// Partner/Together icon
export const TogetherIcon = ({ size = 24, className, ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={cn('text-[#2A8585]', className)}
    {...props}
  >
    <circle cx="8" cy="8" r="3" />
    <circle cx="16" cy="8" r="3" />
    <path d="M3 18C3 14 5 12 8 12C10 12 11.5 13 12 14" />
    <path d="M21 18C21 14 19 12 16 12C14 12 12.5 13 12 14" />
    <path d="M12 14V20" />
  </svg>
);

export default IconContainer;
